import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { PgPalette } from '../core/ui/palette';
import { PgIconComponent } from '../pg-icon/pg-icon.component';
import { styleVariables } from '../styles/variables';

@Component({
  selector: 'pg-badge, grit-badge',
  templateUrl: './pg-badge.component.html',
  styleUrls: ['./pg-badge.component.scss'],
  exportAs: 'gritBadge',
})
export class PgBadgeComponent implements OnInit, AfterViewInit, OnChanges {
  @HostBinding('class.pg-badge')
  _classPgBadge = true;

  @Input()
  get type() {
    return this._type;
  }

  set type(type: PgPalette) {
    const el = this.elementRef.nativeElement as HTMLElement;
    if (this._type) {
      el.classList.remove(this._type);
    }
    const calcType = type || 'default';
    el.classList.add(calcType);
    this._type = calcType;
  }

  @Input()
  get showIcon() {
    return this._showIcon;
  }

  set showIcon(showIcon: boolean) {
    this._showIcon = showIcon;
  }

  protected _type: PgPalette = 'default';
  protected _showIcon: boolean = false;

  @Input()
  progress?: number;

  @Input()
  classes?: Array<string>;

  @ContentChild(PgIconComponent, { static: false })
  iconOverrideChild: PgIconComponent;

  @ViewChild('iconOutlet', { read: ViewContainerRef, static: true })
  iconOutlet: ViewContainerRef;

  @ViewChild('iconOverrideTemplate', { read: TemplateRef })
  iconOverrideTemplate: TemplateRef<any>;

  @ViewChild('warnIconDefault')
  warnIconTemplate: TemplateRef<any>;

  @ViewChild('primaryIconDefault')
  primaryIconTemplate: TemplateRef<any>;

  @ViewChild('infoIconDefault')
  infoIconTemplate: TemplateRef<any>;

  @ViewChild('attentionIconDefault')
  attentionIconTemplate: TemplateRef<any>;

  @ViewChild('successIconDefault')
  successIconTemplate: TemplateRef<any>;

  @ViewChild('attentionStrongIconDefault')
  attentionStrongIconTemplate: TemplateRef<any>;

  @ViewChild('progressIcon')
  progressIconTemplate: TemplateRef<any>;

  @ViewChild('defaultIcon')
  defaultIconTemplate: TemplateRef<any>;

  templateOutletRef: TemplateRef<any>;

  badgeMap = {
    default: {
      template: () => this.defaultIconTemplate,
      foreground: styleVariables.neutral900,
      background: styleVariables.neutral300,
    },
    info: {
      template: () => this.infoIconTemplate,
      foreground: styleVariables.purple500,
      background: styleVariables.purple100,
    },
    primary: {
      template: () => this.primaryIconTemplate,
      foreground: styleVariables.blue500,
      background: styleVariables.blue100,
    },
    success: {
      template: () => this.successIconTemplate,
      foreground: styleVariables.green500,
      background: styleVariables.green100,
    },
    attention: {
      template: () => this.attentionIconTemplate,
      foreground: styleVariables.yellow500,
      background: styleVariables.yellow100,
    },
    'attention-strong': {
      template: () => this.attentionStrongIconTemplate,
      foreground: styleVariables.orange500,
      background: styleVariables.orange100,
    },
    warning: {
      template: () => this.warnIconTemplate,
      foreground: styleVariables.red500,
      background: styleVariables.red100,
    },
  };

  constructor(public elementRef: ElementRef, private _renderer: Renderer2) {}

  ngOnInit() {
    if (!this.type) {
      this.type = 'default';
    }
    if (this.classes) {
      this.addClasses(this.classes);
    }
    if (!this.showIcon) {
      this.showIcon = false;
    }
    if (this.iconOverrideChild) {
      this.showIcon = true;
    }
  }

  ngAfterViewInit() {
    this.defer(() => {
      this._initializeIcon();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.type || changes.progress) {
      this._initializeIcon();
    }

    if (changes.classes && !changes.classes.isFirstChange()) {
      this.removeClasses(changes.classes.previousValue);
      this.addClasses(changes.classes.currentValue);
    }
  }

  private addClasses(classes: Array<string>) {
    const el = this.elementRef.nativeElement as HTMLElement;
    classes.forEach((addedClass: string) => {
      el.classList.add(addedClass);
    });
  }

  private removeClasses(classes: Array<string>) {
    const el = this.elementRef.nativeElement as HTMLElement;
    classes.forEach((addedClass: string) => {
      el.classList.remove(addedClass);
    });
  }

  private _initializeIcon() {
    if (this.iconOverrideChild) {
      this.iconOverrideChild.size = 12;
      this.templateOutletRef = this.iconOverrideTemplate;
    } else if (this.progress || this.progress === 0) {
      this.templateOutletRef = this.progressIconTemplate;
    } else {
      this.templateOutletRef = this.getIconTemplateRefByType(this.type);
    }
  }

  public getIconTemplateRefByType(type: PgPalette) {
    return this.getDataByBadgeType(type).template();
  }

  public getBadgeForeground(type: PgPalette) {
    return this.getDataByBadgeType(type).foreground;
  }
  public getBadgeBackground(type: PgPalette) {
    return this.getDataByBadgeType(type).background;
  }

  private getDataByBadgeType(type: PgPalette) {
    return this.badgeMap[type];
  }

  private defer(fn: Function) {
    setTimeout(fn, 0);
  }
}
